type SoundKey =
    | 'drag-start'
    | 'drag-end'
    | 'connect'
    | 'disconnect'
    | 'delete'
    | 'board-change'
    | 'board-sync'
    | 'dropdown';

const SOUND_PRIORITY: Record<SoundKey, number> = {
    delete: 50,
    disconnect: 40,
    connect: 30,
    'drag-end': 20,
    'drag-start': 20,
    'board-change': 15,
    dropdown: 10,
    'board-sync': 5,
};

const SOUND_KEYS: SoundKey[] = [
    'drag-start',
    'drag-end',
    'connect',
    'disconnect',
    'delete',
    'board-change',
    'board-sync',
    'dropdown',
];
const EXTENSIONS = ['wav', 'mp3'] as const;

const DEBOUNCE_MS = 50;

class BlockSound {
    private theme = 'basic';
    private cache = new Map<string, HTMLAudioElement>();
    private resolved = new Map<SoundKey, string>();
    private resolving = new Map<SoundKey, Promise<void>>();
    private enabled = true;

    private volume = 0.4;
    private volumeByKey: Partial<Record<SoundKey, number>> = {};

    private pendingKey: SoundKey | null = null;
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;

    setTheme(theme: string) {
        if (this.theme === theme) return;
        this.theme = theme;
        this.cache.clear();
        this.resolved.clear();
        this.resolving.clear();
    }

    getTheme() {
        return this.theme;
    }

    setEnabled(enabled: boolean) {
        this.enabled = enabled;
    }

    setVolume(volume: number) {
        this.volume = Math.max(0, Math.min(1, volume));
    }

    setVolumeMultiplier(key: SoundKey, multiplier: number) {
        this.volumeByKey[key] = Math.max(0, multiplier);
    }

    preload() {
        for (const key of SOUND_KEYS) {
            void this.ensureResolved(key);
        }
    }

    queue(key: SoundKey) {
        if (!this.enabled) return;

        if (this.pendingKey === null || SOUND_PRIORITY[key] >= SOUND_PRIORITY[this.pendingKey]) {
            this.pendingKey = key;
        }

        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.flush(), DEBOUNCE_MS);
    }

    private flush() {
        if (this.pendingKey) {
            this.play(this.pendingKey);
            this.pendingKey = null;
        }
        this.debounceTimer = null;
    }

    play(key: SoundKey) {
        if (!this.enabled) return;

        const path = this.resolved.get(key);
        if (!path) {
            void this.ensureResolved(key);
            return;
        }

        let audio = this.cache.get(path);
        if (!audio) {
            audio = new Audio(path);
            audio.preload = 'auto';
            audio.load();
            this.cache.set(path, audio);
        }

        const mul = this.volumeByKey[key] ?? 1;
        audio.volume = Math.max(0, Math.min(1, this.volume * mul));

        try {
            audio.currentTime = 0;
        } catch {}

        audio.play().catch(() => {});
    }

    private ensureResolved(key: SoundKey): Promise<void> {
        const existing = this.resolving.get(key);
        if (existing) return existing;

        const p = this.resolve(key)
            .catch(() => {})
            .finally(() => {
                this.resolving.delete(key);
            });

        this.resolving.set(key, p);
        return p;
    }

    private async resolve(key: SoundKey): Promise<void> {
        const base = `/sounds/block/${this.theme}/${key}`;

        for (const ext of EXTENSIONS) {
            const path = `${base}.${ext}`;

            try {
                const head = await fetch(path, { method: 'HEAD', cache: 'force-cache' });
                if (head.ok) {
                    this.setResolved(key, path);
                    return;
                }
            } catch {}

            try {
                const get = await fetch(path, {
                    method: 'GET',
                    headers: { Range: 'bytes=0-0' },
                    cache: 'force-cache',
                });
                if (get.ok || get.status === 206) {
                    this.setResolved(key, path);
                    return;
                }
            } catch {}
        }
    }

    private setResolved(key: SoundKey, path: string) {
        this.resolved.set(key, path);
        if (!this.cache.has(path)) {
            const audio = new Audio(path);
            audio.preload = 'auto';
            audio.load();
            this.cache.set(path, audio);
        }
    }
}

export const blockSound = new BlockSound();
